// Block scope makes repeated section-script evaluation safe in the theme editor.
if (!customElements.get('tree-retail-locator')) {
  class TreeRetailLocator extends HTMLElement {
    connectedCallback() {
      if (this.connected) return;
      this.form = this.querySelector('[data-retailer-search]');
      this.input = this.querySelector('[data-retailer-query]');
      this.clear = this.querySelector('[data-retailer-clear]');
      this.status = this.querySelector('[data-retailer-status]');
      this.empty = this.querySelector('[data-retailer-empty]');
      this.items = [...this.querySelectorAll('[data-retailer-item]')];
      if (!this.form || !this.input || !this.clear || !this.status || !this.empty) return;

      this.onInput = () => this.filter(this.input.value);
      this.onSubmit = (event) => { event.preventDefault(); this.filter(this.input.value); };
      this.onClear = () => { this.input.value = ''; this.filter(''); this.input.focus(); };
      this.input.addEventListener('input', this.onInput);
      this.form.addEventListener('submit', this.onSubmit);
      this.clear.addEventListener('click', this.onClear);
      this.connected = true;
      this.form.hidden = false;
      this.filter(this.input.value);
      this.mapEl = this.querySelector('[data-retailer-map]');
      if (this.mapEl && this.items.some((item) => item.dataset.retailerLat)) this.loadMap();
    }

    // Leaflet + CARTO light tiles, loaded only on this page. Coordinates are stored in the
    // theme blocks, so the browser never calls a geocoder.
    loadMap() {
      const ready = () => this.initMap();
      if (window.L && window.L.map) { ready(); return; }
      if (!document.querySelector('link[data-tree-leaflet]')) {
        const css = document.createElement('link');
        css.rel = 'stylesheet';
        css.href = 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/leaflet.min.css';
        css.setAttribute('data-tree-leaflet', '');
        document.head.appendChild(css);
      }
      if (!document.querySelector('script[data-tree-leaflet]')) {
        const script = document.createElement('script');
        script.src = 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/leaflet.min.js';
        script.setAttribute('data-tree-leaflet', '');
        script.addEventListener('load', ready);
        script.addEventListener('error', () => { this.mapEl.closest('.tc-retail-locator__map-wrap').hidden = true; });
        document.head.appendChild(script);
      } else {
        document.querySelector('script[data-tree-leaflet]').addEventListener('load', ready);
      }
    }

    initMap() {
      if (this.map || !window.L) return;
      const L = window.L;
      this.map = L.map(this.mapEl, { scrollWheelZoom: false, zoomControl: true, attributionControl: true });
      L.tileLayer(this.mapEl.dataset.tiles, { attribution: this.mapEl.dataset.attribution, maxZoom: 18, minZoom: 6 }).addTo(this.map);
      this.markers = new Map();
      const bounds = [];
      this.items.forEach((item) => {
        const lat = Number.parseFloat(item.dataset.retailerLat);
        const lng = Number.parseFloat(item.dataset.retailerLng);
        if (!Number.isFinite(lat) || !Number.isFinite(lng)) return;
        const approximate = item.dataset.retailerPrecision === 'city';
        const marker = L.circleMarker([lat, lng], {
          radius: approximate ? 9 : 8,
          color: '#175631',
          weight: 2,
          fillColor: approximate ? '#c9992a' : '#175631',
          fillOpacity: approximate ? 0.55 : 0.9,
          dashArray: approximate ? '3 3' : null,
        });
        const title = item.dataset.retailerName || '';
        const place = item.dataset.retailerPlace || '';
        marker.bindTooltip(title, { direction: 'top', offset: [0, -8], opacity: 0.95 });
        marker.bindPopup(`<strong>${this.escape(title)}</strong><br>${this.escape(place)}${approximate ? '<br><em>Approximate location</em>' : ''}<br><a href="#${item.id}" data-retailer-jump="${item.id}">See details</a>`, { closeButton: true, offset: [0, -6] });
        marker.on('click', () => this.highlight(item));
        marker.addTo(this.map);
        this.markers.set(item, marker);
        bounds.push([lat, lng]);
      });
      if (bounds.length) this.map.fitBounds(bounds, { padding: [28, 28], maxZoom: 11 });
      this.map.on('popupopen', (event) => {
        const link = event.popup.getElement()?.querySelector('[data-retailer-jump]');
        link?.addEventListener('click', (e) => {
          e.preventDefault();
          const target = document.getElementById(link.dataset.retailerJump);
          target?.scrollIntoView({ behavior: 'smooth', block: 'start' });
          this.highlight(target);
        });
      });
      this.syncMarkers();
    }

    escape(value) {
      return String(value ?? '').replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]));
    }

    highlight(item) {
      this.items.forEach((el) => el.classList.toggle('is-highlighted', el === item));
    }

    syncMarkers() {
      if (!this.map || !this.markers) return;
      const shown = [];
      this.markers.forEach((marker, item) => {
        const visible = !item.hidden;
        marker.setStyle({ opacity: visible ? 1 : 0.15, fillOpacity: visible ? (item.dataset.retailerPrecision === 'city' ? 0.55 : 0.9) : 0.08 });
        if (visible) shown.push(marker.getLatLng());
      });
      if (shown.length && shown.length < this.markers.size) this.map.fitBounds(shown, { padding: [40, 40], maxZoom: 12 });
      else if (shown.length === this.markers.size && this.allBounds) this.map.fitBounds(this.allBounds, { padding: [28, 28], maxZoom: 11 });
      if (!this.allBounds && shown.length === this.markers.size) this.allBounds = shown;
    }

    disconnectedCallback() {
      if (!this.connected) return;
      this.input.removeEventListener('input', this.onInput);
      this.form.removeEventListener('submit', this.onSubmit);
      this.clear.removeEventListener('click', this.onClear);
      if (this.map) { this.map.remove(); this.map = null; this.markers = null; this.allBounds = null; }
      this.connected = false;
    }

    normalize(value) {
      return String(value ?? '').normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase().trim().replace(/\s+/g, ' ');
    }

    filter(value) {
      const query = this.normalize(value);
      const words = query.split(' ').filter(Boolean);
      let count = 0;
      this.items.forEach((item) => {
        const text = this.normalize(item.dataset.retailerText);
        const visible = words.every((word) => text.includes(word));
        item.hidden = !visible;
        if (visible) count += 1;
      });
      this.clear.hidden = query.length === 0;
      this.empty.hidden = count > 0;
      this.status.textContent = `Showing ${count}${query ? ' matching' : ''} retailer${count === 1 ? '' : 's'}.`;
      this.syncMarkers();
    }
  }

  customElements.define('tree-retail-locator', TreeRetailLocator);
}
